import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { MainContentPanel } from 'features/bpDetails/BpDetails'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import {
  checkReportingAccessRoutine,
  triggerReportingReportRoutine,
  getReportingReportsRoutine,
  getReportingReportRoutine,
  pollReportingReportRoutine,
  clearReportingRoutine,
  stopPollingReportingRoutine,
  updateReportingAnswerRoutine,
  regenerateReportingAnswerRoutine,
  exportReportingPdfRoutine,
  exportReportingWordRoutine,
  exportReportingCsvRoutine
} from 'ducks/reporting/actions'
import {
  selectHasReportingAccess,
  selectCurrentReport,
  selectReports,
  selectIsLoadingReport,
  selectIsLoadingReports,
  selectIsTriggering,
  selectIsPolling,
  selectIsReportCompleted,
  selectIsReportProcessing,
  selectIsReportFailed,
  selectReportAnswersByCategory,
  selectCanTriggerReport,
  selectReportingAccess,
  selectIsUpdatingAnswer,
  selectEditingQuestionId,
  selectIsRegeneratingAnswer,
  selectRegeneratingQuestionId,
  selectIsExportingPdf,
  selectIsExportingWord,
  selectIsExportingCsv
} from 'ducks/reporting/selectors'
import ReportHeader from './ReportHeader'
import ReportAnswerCard from './ReportAnswerCard'
import ParticularsCard from './ParticularsCard'
import ReportLoadingState from './ReportLoadingState'
import DocumentSuggestionPanel from './DocumentSuggestionPanel'
import ReportVersionSelector from './ReportVersionSelector'
import ReportEmailModal from './ReportEmailModal'
import { Alert, Skeleton, Collapse, IconButton, Tooltip } from '@mui/material'
import AssessmentIcon from '@mui/icons-material/Assessment'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import LockIcon from '@mui/icons-material/Lock'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'

const ReportPage = ({ reportType = 'insurance' }) => {
  const dispatch = useDispatch()
  const bp = useSelector(getSelectedBp)
  const hasAccess = useSelector(selectHasReportingAccess)
  const accessInfo = useSelector(selectReportingAccess)
  const currentReport = useSelector(selectCurrentReport)
  const reports = useSelector(selectReports)
  const isLoadingReport = useSelector(selectIsLoadingReport)
  const isLoadingReports = useSelector(selectIsLoadingReports)
  const isTriggering = useSelector(selectIsTriggering)
  const isPolling = useSelector(selectIsPolling)
  const isCompleted = useSelector(selectIsReportCompleted)
  const isProcessing = useSelector(selectIsReportProcessing)
  const isFailed = useSelector(selectIsReportFailed)
  const answersByCategory = useSelector(selectReportAnswersByCategory)
  const canTrigger = useSelector(selectCanTriggerReport)
  const isUpdatingAnswer = useSelector(selectIsUpdatingAnswer)
  const editingQuestionId = useSelector(selectEditingQuestionId)
  const isRegeneratingAnswer = useSelector(selectIsRegeneratingAnswer)
  const regeneratingQuestionId = useSelector(selectRegeneratingQuestionId)
  const isExportingPdf = useSelector(selectIsExportingPdf)
  const isExportingWord = useSelector(selectIsExportingWord)
  const isExportingCsv = useSelector(selectIsExportingCsv)

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentFile, setCurrentFile] = useState(null)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})
  const [retryingAllMissing, setRetryingAllMissing] = useState(false)
  const categoriesInitializedRef = useRef(false)
  const lastReportIdRef = useRef(null)

  // Derive report-type display info from the access check response (DB-driven).
  // Falls back to generic strings until the access check resolves.
  const reportTypeInfo = accessInfo?.reportTypes?.find(t => t.slug === reportType)
  const typeConfig = {
    label: 'Reports',
    title: currentReport?.reportType?.name || reportTypeInfo?.name || 'Report',
    description: reportTypeInfo?.description
      || `Generate a report for ${bp?.name || 'this Building Passport'} based on its documents.`,
  }

  useEffect(() => {
    if (bp?.id) {
      dispatch(checkReportingAccessRoutine.trigger({ bpId: bp.id }))
      dispatch(getReportingReportsRoutine.trigger({ bpId: bp.id, reportType }))
    }

    return () => {
      dispatch(stopPollingReportingRoutine.trigger())
      dispatch(clearReportingRoutine.trigger())
      categoriesInitializedRef.current = false
      lastReportIdRef.current = null
    }
  }, [bp?.id, reportType, dispatch])

  // Load the latest report when reports are fetched
  useEffect(() => {
    if (reports.length > 0 && !currentReport && bp?.id) {
      const latestReport = reports[0]
      dispatch(getReportingReportRoutine.trigger({
        bpId: bp.id,
        reportId: latestReport.id
      }))
    }
  }, [reports, currentReport, bp?.id, dispatch])

  // Initialize all categories as expanded on first load or when user manually switches report version.
  // Skip reset during regeneration (which creates a new report version automatically).
  useEffect(() => {
    if (answersByCategory && Object.keys(answersByCategory).length > 0) {
      const reportId = currentReport?.id
      const isNewReport = reportId && reportId !== lastReportIdRef.current
      lastReportIdRef.current = reportId

      if (!categoriesInitializedRef.current) {
        // First load — expand all
        const initialExpanded = {}
        Object.keys(answersByCategory).forEach(category => {
          initialExpanded[category] = true
        })
        setExpandedCategories(initialExpanded)
        categoriesInitializedRef.current = true
      } else if (isNewReport && !isRegeneratingAnswer) {
        // User manually switched report version — expand all
        const initialExpanded = {}
        Object.keys(answersByCategory).forEach(category => {
          initialExpanded[category] = true
        })
        setExpandedCategories(initialExpanded)
      }
      // During regeneration or same-report updates: preserve existing expand/collapse state
    }
  }, [answersByCategory, currentReport?.id, isRegeneratingAnswer])

  // Auto-start polling when we load a report that's still processing/pending
  // (e.g. navigating to the page while a report is generating, or page reload)
  useEffect(() => {
    if (currentReport && (currentReport.isProcessing || currentReport.isPending) && !isPolling && bp?.id) {
      dispatch(pollReportingReportRoutine.trigger({
        bpId: bp.id,
        reportId: currentReport.id
      }))
    }
  }, [currentReport?.id, currentReport?.isProcessing, currentReport?.isPending, isPolling, bp?.id, dispatch])

  const handleTriggerReport = () => {
    dispatch(triggerReportingReportRoutine.trigger({ bpId: bp.id, reportType }))
  }

  const handleVersionSelect = (reportId) => {
    dispatch(getReportingReportRoutine.trigger({
      bpId: bp.id,
      reportId
    }))
  }

  const handleSourceClick = (source) => {
    if (!source?.fileUrl) return

    setCurrentFile({
      url: source.fileUrl,
      displayName: source.name,
      id: source.id || 'source-file',
      contentType: source.name?.toLowerCase().endsWith('.pdf')
        ? 'application/pdf'
        : null,
      versions: source.versions || [],
    })
    setPdfViewerOpen(true)
  }

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  const handleEditAnswer = (questionId, newAnswer) => {
    dispatch(updateReportingAnswerRoutine.trigger({
      bpId: bp.id,
      reportId: currentReport.id,
      questionId,
      answer: newAnswer
    }))
  }

  const handleRegenerateAnswer = (questionId, feedback, customInstruction, subKey = null) => {
    dispatch(regenerateReportingAnswerRoutine.trigger({
      bpId: bp.id,
      reportId: currentReport.id,
      questionId,
      feedback,
      customInstruction,
      subKey
    }))
  }

  const handleExportPdf = () => {
    dispatch(exportReportingPdfRoutine.trigger({
      bpId: bp.id,
      reportId: currentReport.id,
      buildingName: bp.name,
      reportTitle: currentReport?.reportType?.name || typeConfig.title
    }))
  }

  const handleExportWord = () => {
    dispatch(exportReportingWordRoutine.trigger({
      bpId: bp.id,
      reportId: currentReport.id,
      buildingName: bp.name,
      reportTitle: currentReport?.reportType?.name || typeConfig.title
    }))
  }

  const handleExportCsv = () => {
    dispatch(exportReportingCsvRoutine.trigger({
      bpId: bp.id,
      reportId: currentReport.id,
      buildingName: bp.name,
      reportTitle: currentReport?.reportType?.name || typeConfig.title
    }))
  }

  // Collect all answers that have no data found
  const missingAnswers = answersByCategory
    ? Object.values(answersByCategory).flatMap(cat =>
        cat.answers.filter(a => {
          if (a.noInfoFound) return true
          if (!a.answer) return true
          // Structured answers (CPSE.7 sub-enquiry maps) — "missing" means every sub-value is empty.
          if (typeof a.answer !== 'string') {
            return !Object.values(a.answer).some(v => typeof v === 'string' && v.trim() !== '')
          }
          const lower = a.answer.toLowerCase()
          return lower.includes('no information found') || lower.includes('not found in the documents')
        })
      )
    : []

  const handleRetryAllMissing = async () => {
    if (missingAnswers.length === 0) return
    setRetryingAllMissing(true)
    for (const answer of missingAnswers) {
      dispatch(regenerateReportingAnswerRoutine.trigger({
        bpId: bp.id,
        reportId: currentReport.id,
        questionId: answer.questionId,
        feedback: 'try_again'
      }))
    }
    setRetryingAllMissing(false)
  }

  // No access state
  if (!hasAccess) {
    return (
      <PageWrapper>
        <MainContentPanel>
          <NoAccessWrapper>
            <NoAccessContent>
              <NoAccessLabel>{typeConfig.label}</NoAccessLabel>
              <NoAccessTitle>Access required</NoAccessTitle>
              <NoAccessDescription>
                This feature analyses your building documents and generates detailed
                reports. Contact your administrator to enable access.
              </NoAccessDescription>
            </NoAccessContent>
            <NoAccessVisual>
              <LockIcon sx={{ fontSize: 48, color: '#9CA3AF' }} />
            </NoAccessVisual>
          </NoAccessWrapper>
        </MainContentPanel>
      </PageWrapper>
    )
  }

  // Loading initial data (either loading reports list or loading a specific report)
  if ((isLoadingReport || isLoadingReports) && !currentReport) {
    return (
      <PageWrapper>
        <MainContentPanel>
          <LoadingContainer>
            <LoadingHeader>
              <SkeletonIcon />
              <LoadingHeaderContent>
                <Skeleton variant="text" width="60%" height={36} />
                <Skeleton variant="text" width="40%" height={24} />
              </LoadingHeaderContent>
            </LoadingHeader>
            <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 2, mb: 3 }} />
            <LoadingCards>
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={180} sx={{ borderRadius: 2 }} />
            </LoadingCards>
          </LoadingContainer>
        </MainContentPanel>
      </PageWrapper>
    )
  }

  // No reports yet - show empty state (only after reports list has loaded)
  if (!currentReport && !isTriggering && !isPolling && !isLoadingReports) {
    return (
      <PageWrapper>
        <MainContentPanel>
          <EmptyStateWrapper>
            <EmptyStateHeader>
              <HeaderIcon>
                <AssessmentIcon />
              </HeaderIcon>
              <HeaderText>
                <EmptyStateLabel>{typeConfig.label}</EmptyStateLabel>
                <EmptyStateTitle>{typeConfig.title}</EmptyStateTitle>
              </HeaderText>
            </EmptyStateHeader>

            <EmptyStateBody>
              <EmptyStateMain>
                <EmptyStateDescription>
                  {typeConfig.description} The report analyses documentation for <strong>{bp.name}</strong> and cites the underlying sources.
                </EmptyStateDescription>

                <ReportPreview>
                  <PreviewHeader>
                    <PreviewDot status="success" />
                    <PreviewDot status="success" />
                    <PreviewDot status="pending" />
                    <PreviewLabel>Report Preview</PreviewLabel>
                  </PreviewHeader>
                  <PreviewContent>
                    <PreviewSection>
                      <PreviewSectionTitle />
                      <PreviewLine width="90%" />
                      <PreviewLine width="75%" />
                    </PreviewSection>
                    <PreviewSection>
                      <PreviewSectionTitle />
                      <PreviewLine width="85%" />
                      <PreviewLine width="60%" />
                    </PreviewSection>
                  </PreviewContent>
                </ReportPreview>
              </EmptyStateMain>

              <EmptyStateFooter>
                <FooterInfo>
                  <InfoTag>
                    <VerifiedUserIcon sx={{ fontSize: 16 }} />
                    <span>Cited sources</span>
                  </InfoTag>
                  <InfoTag>
                    <AssessmentIcon sx={{ fontSize: 16 }} />
                    <span>Comprehensive coverage</span>
                  </InfoTag>
                </FooterInfo>
                <EmptyStateAction>
                  <GenerateButton
                    onClick={handleTriggerReport}
                    disabled={!canTrigger}
                  >
                    Generate Report
                  </GenerateButton>
                  <EstimatedTime>~3 min</EstimatedTime>
                </EmptyStateAction>
              </EmptyStateFooter>
            </EmptyStateBody>
          </EmptyStateWrapper>
        </MainContentPanel>
      </PageWrapper>
    )
  }

  // Report is processing
  if (isProcessing || isTriggering || isPolling) {
    return (
      <PageWrapper>
        <MainContentPanel>
          <ReportLoadingState
            reportVersion={currentReport?.version}
            status={currentReport?.status}
            reportTitle={typeConfig.title}
            liveProgress={currentReport?.metadata?.progress}
          />
        </MainContentPanel>
      </PageWrapper>
    )
  }

  // Report failed
  if (isFailed) {
    return (
      <PageWrapper>
        <MainContentPanel>
          <FailedContainer>
            <FailedCard>
              <FailedIconWrapper>
                <RefreshIcon sx={{ fontSize: 40, color: 'white' }} />
              </FailedIconWrapper>
              <Alert severity="error" sx={{ mb: 3, width: '100%', borderRadius: 2 }}>
                <AlertTitle>Report Generation Failed</AlertTitle>
                {currentReport?.errorMessage || 'An unexpected error occurred while generating the report.'}
              </Alert>
              <RetryButton
                onClick={handleTriggerReport}
                disabled={!canTrigger}
              >
                <RefreshIcon />
                Try Again
              </RetryButton>
            </FailedCard>
          </FailedContainer>
        </MainContentPanel>
      </PageWrapper>
    )
  }

  // Report completed - show results
  return (
    <PageWrapper>
      <MainContentPanel>
        <ReportHeader
          report={currentReport}
          buildingName={bp.name}
          reportType={reportType}
          reportTypeName={typeConfig.title}
          onGenerateNew={handleTriggerReport}
          canGenerateNew={canTrigger}
          onSendEmail={() => setEmailModalOpen(true)}
          onExportPdf={String(reportType).startsWith('cpse') ? undefined : handleExportPdf}
          onExportWord={handleExportWord}
          onExportCsv={handleExportCsv}
          isExportingPdf={isExportingPdf}
          isExportingWord={isExportingWord}
          isExportingCsv={isExportingCsv}
        />

        {/* <DocumentSuggestionPanel reportType={reportType} /> */}

        {reports.length > 1 && (
          <VersionSelectorWrapper>
            <ReportVersionSelector
              reports={reports}
              currentReportId={currentReport?.id}
              onSelect={handleVersionSelect}
            />
          </VersionSelectorWrapper>
        )}

        {missingAnswers.length > 0 && (
          <RetryAllBanner>
            <RetryAllInfo>
              <RetryAllCount>{missingAnswers.length}</RetryAllCount>
              <RetryAllText>
                {missingAnswers.length === 1
                  ? 'answer could not be found in the documents'
                  : 'answers could not be found in the documents'}
              </RetryAllText>
            </RetryAllInfo>
            <RetryAllButton
              onClick={handleRetryAllMissing}
              disabled={isRegeneratingAnswer || retryingAllMissing}
            >
              <ReplayIcon sx={{ fontSize: 18 }} />
              {isRegeneratingAnswer ? 'Retrying...' : `Retry all missing`}
            </RetryAllButton>
          </RetryAllBanner>
        )}

        {reportType === 'cpse7' && currentReport && (
          <ParticularsCard bpId={bp.id} reportId={currentReport.id} />
        )}

        <AnswersContainer>
          {Object.entries(answersByCategory).map(([category, categoryData]) => (
            <CategorySection key={category}>
              <CategoryHeader onClick={() => toggleCategory(category)}>
                <CategoryTitleWrapper>
                  <CategoryBadge>{categoryData.answers.length}</CategoryBadge>
                  <CategoryTitle>{categoryData.label}</CategoryTitle>
                </CategoryTitleWrapper>
                <Tooltip title={expandedCategories[category] ? 'Collapse section' : 'Expand section'}>
                  <ExpandIconButton size="small">
                    {expandedCategories[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ExpandIconButton>
                </Tooltip>
              </CategoryHeader>
              <Collapse in={expandedCategories[category]} timeout="auto">
                <AnswersList>
                  {categoryData.answers.map((answer, index) => (
                    <ReportAnswerCard
                      key={answer.questionId || index}
                      answer={answer}
                      onSourceClick={handleSourceClick}
                      onEdit={handleEditAnswer}
                      onRegenerate={handleRegenerateAnswer}
                      isEditing={editingQuestionId === answer.questionId && isUpdatingAnswer}
                      isRegenerating={regeneratingQuestionId === answer.questionId && isRegeneratingAnswer}
                      regeneratingSubKey={regeneratingQuestionId === answer.questionId ? (currentReport?.metadata?.regeneratingSubKey || null) : null}
                      canEdit={!isProcessing && !isUpdatingAnswer && !isRegeneratingAnswer}
                    />
                  ))}
                </AnswersList>
              </Collapse>
            </CategorySection>
          ))}
        </AnswersContainer>
      </MainContentPanel>

      <PdfFullScreenViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        file={currentFile}
      />

      <ReportEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        bpId={bp.id}
        reportId={currentReport?.id}
        buildingName={bp.name}
      />
    </PageWrapper>
  )
}

export default ReportPage

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const PageWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
`

// Loading State Styles
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`

const LoadingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`

const SkeletonIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%);
  background-size: 200% 200%;
  animation: shimmer 1.5s infinite;
  border-radius: 16px;
  flex-shrink: 0;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`

const LoadingHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const LoadingCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// No Access State Styles
const NoAccessWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  padding: 60px 0;
  min-height: 300px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 32px;
    padding: 40px 0;
  }
`

const NoAccessContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 500px;
`

const NoAccessLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const NoAccessTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;

  @media (max-width: 600px) {
    font-size: 24px;
  }
`

const NoAccessDescription = styled.p`
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;

  @media (max-width: 600px) {
    font-size: 15px;
  }
`

const NoAccessVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: ${({ theme }) => theme.colors.lightGrey}60;
  border-radius: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    border-radius: 20px;
  }
`

// Empty State Styles
const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 8px 0;
`

const EmptyStateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const HeaderIcon = styled.div`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, #B83D1F 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(210, 71, 36, 0.25);

  svg {
    font-size: 28px;
  }

  @media (max-width: 600px) {
    width: 48px;
    height: 48px;
    border-radius: 12px;

    svg {
      font-size: 24px;
    }
  }
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const EmptyStateLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const EmptyStateTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`

const EmptyStateBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`

const EmptyStateMain = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 40px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const EmptyStateDescription = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0;

  strong {
    color: ${({ theme }) => theme.colors.haiti};
    font-weight: 600;
  }

  @media (max-width: 600px) {
    font-size: 14px;
  }
`

const ReportPreview = styled.div`
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 900px) {
    max-width: 320px;
  }
`

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.backgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const PreviewDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) => status === 'success' ? '#10B981' : '#E5E7EB'};
`

const PreviewLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const PreviewContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PreviewSectionTitle = styled.div`
  width: 40%;
  height: 8px;
  background: ${({ theme }) => theme.colors.primary}30;
  border-radius: 4px;
`

const PreviewLine = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 3px;
  width: ${({ width }) => width || '100%'};
`

const EmptyStateFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`

const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 600px) {
    justify-content: center;
  }
`

const InfoTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const EmptyStateAction = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 600px) {
    justify-content: center;
  }
`

const GenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(210, 71, 36, 0.2);

  &:hover:not(:disabled) {
    background: #B83D1F;
    box-shadow: 0 4px 12px rgba(210, 71, 36, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 600px) {
    flex: 1;
  }
`

const EstimatedTime = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

// Failed State Styles
const FailedContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 24px;
`

const FailedCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 480px;
  padding: 40px 32px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  @media (max-width: 600px) {
    padding: 32px 24px;
  }
`

const FailedIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
  border-radius: 50%;
  margin-bottom: 20px;
`

const AlertTitle = styled.strong`
  display: block;
  margin-bottom: 4px;
`

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(51, 190, 204, 0.3);

  svg {
    font-size: 20px;
  }

  &:hover:not(:disabled) {
    background: #2BA8B9;
    box-shadow: 0 6px 16px rgba(51, 190, 204, 0.4);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    transform: scale(0.98);
  }

  &:disabled {
    background: #D1D5DB;
    cursor: not-allowed;
    box-shadow: none;
  }
`

// Report View Styles
const VersionSelectorWrapper = styled.div`
  margin-bottom: 28px;
  animation: ${fadeIn} 0.3s ease-out;
`

const AnswersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const CategorySection = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 600px) {
    border-radius: 12px;
  }
`

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}08, transparent);
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}12, ${({ theme }) => theme.colors.primary}04);
  }

  @media (max-width: 600px) {
    padding: 16px 20px;
  }
`

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 600;
`

const CategoryTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`

const ExpandIconButton = styled(IconButton)`
  && {
    color: ${({ theme }) => theme.colors.grey[200]};

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.primary}10;
    }
  }
`

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: ${({ theme }) => theme.colors.lightGrey};
`

const RetryAllBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #FFF7ED;
  border: 1px solid #FDBA74;
  border-radius: 12px;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`

const RetryAllInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const RetryAllCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  background: #F97316;
  color: white;
  border-radius: 13px;
  font-size: 13px;
  font-weight: 600;
`

const RetryAllText = styled.span`
  font-size: 14px;
  color: #9A3412;
  font-weight: 500;
`

const RetryAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  color: white;
  background: #F97316;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #EA580C;
  }

  &:disabled {
    background: #FDBA74;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    justify-content: center;
  }
`
